
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Check, Copy, Info, ShieldAlert, BrainCircuit, Zap, AlertOctagon } from 'lucide-react';

interface ActionButton {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'secondary' | 'danger' | 'outline' | 'neural';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: React.ReactNode;
  icon?: 'warning' | 'info' | 'success' | 'duplicate' | 'danger' | 'neural';
  actions: ActionButton[];
}

export const CentralAlert: React.FC<Props> = ({ isOpen, onClose, title, message, icon = 'info', actions }) => {
  if (!isOpen) return null;

  const getConfig = () => {
    switch (icon) {
      case 'warning':
        return {
          icon: <AlertTriangle className="w-8 h-8" />,
          color: 'text-status-warning',
          bg: 'bg-status-warning/10',
          border: 'border-status-warning/30',
          shadow: 'shadow-xl',
          btn: 'bg-status-warning text-badge-text-inv hover:brightness-110',
          line: 'bg-status-warning'
        };
      case 'duplicate':
        return {
          icon: <Copy className="w-8 h-8" />,
          color: 'text-sal-cyber-teal',
          bg: 'bg-sal-cyber-teal/10',
          border: 'border-sal-cyber-teal/30',
          shadow: 'shadow-xl',
          btn: 'bg-sal-cyber-teal text-badge-text-inv hover:brightness-110',
          line: 'bg-sal-cyber-teal'
        };
      case 'success':
        return {
          icon: <Check className="w-8 h-8" />,
          color: 'text-status-ready',
          bg: 'bg-status-ready/10',
          border: 'border-status-ready/30',
          shadow: 'shadow-xl',
          btn: 'bg-status-ready text-badge-text-inv hover:brightness-110',
          line: 'bg-status-ready'
        };
      case 'danger':
        return {
          icon: <AlertOctagon className="w-8 h-8" />,
          color: 'text-status-critical',
          bg: 'bg-status-critical/10',
          border: 'border-status-critical/30',
          shadow: 'shadow-xl',
          btn: 'bg-status-critical text-badge-text-inv hover:brightness-110',
          line: 'bg-status-critical'
        };
      case 'neural':
        return {
          icon: <BrainCircuit className="w-8 h-8" />,
          color: 'text-status-info',
          bg: 'bg-status-info/10',
          border: 'border-status-info/30',
          shadow: 'shadow-xl',
          btn: 'bg-status-info text-badge-text-inv hover:brightness-110',
          line: 'bg-status-info'
        };
      default:
        return {
          icon: <Zap className="w-8 h-8" />,
          color: 'text-sal-cyber-teal',
          bg: 'bg-card-bg/80',
          border: 'border-border-dim',
          shadow: 'shadow-xl',
          btn: 'bg-sal-cyber-teal text-badge-text-inv hover:brightness-110',
          line: 'bg-sal-cyber-teal'
        };
    }
  };

  const style = getConfig();

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[130000] flex items-center justify-center p-4">
        {/* Backdrop Deep Blur */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-[rgba(var(--overlay-rgba))] backdrop-blur-md"
        />

        {/* Modal Card - Cockpit Aesthetic */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className={`relative w-full max-w-md rounded-[2.5rem] overflow-hidden border backdrop-blur-2xl bg-[rgb(var(--modal-bg))] border-[rgb(var(--modal-border))] ${style.shadow}`}
        >
          <div className="p-8 flex flex-col items-center text-center relative">
            {/* Close Button Top Right */}
            <button onClick={onClose} className="absolute top-6 right-6 text-text-muted hover:text-text-primary transition-colors bg-black/5 dark:bg-white/5 rounded-xl p-2">
              <X className="w-5 h-5" />
            </button>

            {/* Stylized Icon Container */}
            <div className={`w-20 h-20 bg-black/5 dark:bg-black/40 rounded-3xl flex items-center justify-center mb-6 shadow-inner border border-border-dim ${style.color}`}>
              {style.icon}
            </div>

            {/* Typography - High Contrast */}
            <h3 className="text-3xl font-black text-text-primary leading-tight uppercase tracking-tighter mb-4">
              {title}
            </h3>

            <div className="text-text-muted text-base font-medium leading-relaxed mb-8 px-4">
              {message}
            </div>

            {/* Action Stack */}
            <div className="flex flex-col gap-3 w-full">
              {actions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={action.onClick}
                  className={`w-full py-4 rounded-2xl font-black text-sm uppercase tracking-[0.2em] transition-all active:scale-[0.98] shadow-lg
                        ${action.variant === 'primary' ? style.btn : ''}
                        ${action.variant === 'secondary' ? 'bg-sal-gold text-sal-forest hover:brightness-110 shadow-sal-gold/10' : ''}
                        ${action.variant === 'danger' ? 'bg-status-critical text-badge-text-inv hover:brightness-110' : ''}
                        ${action.variant === 'outline' ? 'bg-black/5 dark:bg-white/5 border border-border-dim text-text-muted hover:bg-black/10 dark:hover:bg-white/10' : ''}
                        ${action.variant === 'neural' ? 'bg-status-info text-badge-text-inv hover:brightness-110' : ''}
                      `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>

          {/* System Flair: Animated Progress Bar Base */}
          <div className="h-1.5 w-full bg-black/10 dark:bg-black/20">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 0.4 }}
              className={`h-full ${style.line}`}
            />
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
